import { useMutation, useQuery } from "@apollo/client/react"
import { useEffect, useState } from "react"
import { Link, useBlocker } from "react-router-dom"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import type { ExperienceProfileFieldsFragment } from "~/__generated__/graphql"
import { formatDate } from "~/common/date"
import { profilePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { PageLayout } from "~/layouts/page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"

gql(/* GraphQL */ `
  fragment ExperienceProfileFields on ExperienceProfile {
    id
    markdown
    timeZone
    fridayTime
    postsPerWeek
    lockVersion
    slackChannelId
    slackRemindersEnabled
    revisions {
      id
      number
      markdown
      timeZone
      fridayTime
      postsPerWeek
      slackChannelId
      slackRemindersEnabled
      createdAt
    }
  }
`)
const PROFILE = gql(/* GraphQL */ `
  query MyExperienceProfile {
    experienceProfile {
      ...ExperienceProfileFields
    }
  }
`)
const SAVE_PROFILE = gql(/* GraphQL */ `
  mutation SaveExperienceProfile($input: ExperienceProfileSaveInput!) {
    experienceProfileSave(input: $input) {
      profile {
        ...ExperienceProfileFields
      }
    }
  }
`)

const TEMPLATE = `# My experience

## Topics I can speak about firsthand

## Stories and examples
For each story: what happened, my role, what I learned, and what I can share publicly.

## My point of view

## How I sound

## Topics or details to avoid
`

function download(markdown: string, version: number) {
  const url = URL.createObjectURL(new Blob([markdown], { type: "text/markdown;charset=utf-8" }))
  const link = document.createElement("a")
  link.href = url
  link.download = `experience-profile-v${version}.md`
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function ExperienceProfileScreen() {
  useDocumentTitle("My experience & cadence")
  const { data, loading, error, refetch } = useQuery(PROFILE, { fetchPolicy: "network-only" })
  return (
    <PageLayout title="My experience & cadence">
      <Link to={profilePath({})} className="text-sm underline">
        Back to my account
      </Link>
      <p className="my-4 max-w-3xl text-muted-foreground">
        Keep your own stories, expertise and voice here. Each partner has a separate profile and
        posting cadence. Only you can view or edit yours.
      </p>
      {error && <GraphqlError error={error} />}
      {loading && !data ? (
        <p>Loading your profile…</p>
      ) : (
        data && (
          <ProfileForm
            initial={data.experienceProfile ?? null}
            reload={async () => (await refetch()).data?.experienceProfile ?? null}
          />
        )
      )}
    </PageLayout>
  )
}

function ProfileForm({
  initial,
  reload,
}: {
  initial: ExperienceProfileFieldsFragment | null
  reload: () => Promise<ExperienceProfileFieldsFragment | null>
}) {
  const [saved, setSaved] = useState(initial)
  const [markdown, setMarkdown] = useState(initial?.markdown ?? "")
  const [timeZone, setTimeZone] = useState(initial?.timeZone ?? "America/New_York")
  const [fridayTime, setFridayTime] = useState(initial?.fridayTime ?? "09:00")
  const [postsPerWeek, setPostsPerWeek] = useState(initial?.postsPerWeek ?? 3)
  const [slackChannelId, setSlackChannelId] = useState(initial?.slackChannelId ?? "")
  const [slackEnabled, setSlackEnabled] = useState(initial?.slackRemindersEnabled ?? false)
  const [importError, setImportError] = useState("")
  const [save, { loading: saving, error }] = useMutation(SAVE_PROFILE)
  const dirty =
    markdown !== (saved?.markdown ?? "") ||
    timeZone !== (saved?.timeZone ?? "America/New_York") ||
    fridayTime !== (saved?.fridayTime ?? "09:00") ||
    postsPerWeek !== (saved?.postsPerWeek ?? 3) ||
    slackChannelId !== (saved?.slackChannelId ?? "") ||
    slackEnabled !== (saved?.slackRemindersEnabled ?? false)
  const blocker = useBlocker(dirty)
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("Leave without saving your profile changes?")) blocker.proceed()
      else blocker.reset()
    }
  }, [blocker])
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [dirty])

  function loadProfile(profile: ExperienceProfileFieldsFragment | null) {
    setSlackChannelId(profile?.slackChannelId ?? "")
    setSlackEnabled(profile?.slackRemindersEnabled ?? false)
    setSaved(profile)
    setMarkdown(profile?.markdown ?? "")
    setTimeZone(profile?.timeZone ?? "America/New_York")
    setFridayTime(profile?.fridayTime ?? "09:00")
    setPostsPerWeek(profile?.postsPerWeek ?? 3)
  }

  return (
    <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)]">
      <form
        className="min-w-0 space-y-6"
        onSubmit={async (event) => {
          event.preventDefault()
          try {
            const result = await save({
              variables: {
                input: {
                  markdown,
                  timeZone,
                  fridayTime,
                  postsPerWeek,
                  slackChannelId: slackChannelId || null,
                  slackRemindersEnabled: slackEnabled,
                  expectedVersion: saved?.lockVersion,
                },
              },
            })
            if (result.data) {
              loadProfile(result.data.experienceProfileSave.profile)
              toast.success("Experience profile saved")
            }
          } catch {
            /* Displayed by GraphqlError. */
          }
        }}
      >
        <section className="space-y-3 rounded-xl border p-5">
          <label htmlFor="experience-markdown" className="font-semibold">
            My experience (Markdown)
          </label>
          <p className="text-sm text-muted-foreground">
            Write only experiences that belong to you. No partner stories are copied into this
            profile. Your saved profile helps match source stories and ground your drafts.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => {
                if (
                  !markdown ||
                  window.confirm(
                    "Replace the editor with a blank outline? Your saved version stays in history."
                  )
                )
                  setMarkdown(TEMPLATE)
              }}
            >
              Use blank outline
            </Button>
            <label className="text-sm">
              Import Markdown
              <input
                aria-label="Import Markdown"
                type="file"
                accept=".md,.markdown,.txt,text/markdown,text/plain"
                className="mt-1 block max-w-full text-sm"
                disabled={saving}
                onChange={async (event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ""
                  if (!file) return
                  setImportError("")
                  if (file.size > 200_000) {
                    setImportError("Choose a text file smaller than 200 KB.")
                    return
                  }
                  try {
                    const text = await file.text()
                    if (text.length > 50_000 || text.includes("\0")) {
                      setImportError("Use plain Markdown with at most 50,000 characters.")
                      return
                    }
                    if (
                      !markdown ||
                      window.confirm(
                        "Replace the editor with this file? Save to create a new version."
                      )
                    )
                      setMarkdown(text)
                  } catch {
                    setImportError("Could not read that file. Try again.")
                  }
                }}
              />
            </label>
          </div>
          {importError && (
            <p role="alert" className="text-sm text-destructive">
              {importError}
            </p>
          )}
          <textarea
            id="experience-markdown"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            maxLength={50000}
            rows={18}
            disabled={saving}
            className="w-full rounded-md border bg-background p-3 font-mono text-sm"
            placeholder="Your experience, examples, opinions and voice…"
          />
        </section>
        <section className="space-y-4 rounded-xl border p-5">
          <h2 className="font-semibold">My recording & posting rhythm</h2>
          <p className="text-sm text-muted-foreground">
            Friday batch reminders and the publishing calendar will use these preferences once
            connected. This does not enable Slack messages yet.
          </p>
          <label className="block text-sm">
            Time zone
            <input
              className="mt-1 block h-10 w-full rounded-md border bg-background px-3 text-sm"
              aria-label="Time zone"
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              required
              disabled={saving}
              list="profile-time-zones"
            />
            <datalist id="profile-time-zones">
              {Intl.supportedValuesOf("timeZone").map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </label>
          <label className="block text-sm">
            Friday recording time
            <input
              className="mt-1 block h-10 w-full rounded-md border bg-background px-3 text-sm"
              aria-label="Friday recording time"
              type="time"
              value={fridayTime}
              onChange={(e) => setFridayTime(e.target.value)}
              required
              disabled={saving}
            />
          </label>
          <label className="block text-sm">
            LinkedIn feed posts per week
            <input
              className="mt-1 block h-10 w-full rounded-md border bg-background px-3 text-sm"
              aria-label="LinkedIn feed posts per week"
              type="number"
              min={1}
              max={14}
              step={1}
              value={postsPerWeek}
              onChange={(e) => setPostsPerWeek(Number(e.target.value))}
              required
              disabled={saving}
            />
          </label>
        </section>
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h2 className="font-semibold">Slack reminders</h2>
          <p className="text-sm text-muted-foreground">
            Friday recording nudges and reminders for scheduled posts. No daily topic messages. A
            server bot token and a channel or direct-message destination are required.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={slackEnabled}
              disabled={saving}
              onChange={(event) => setSlackEnabled(event.target.checked)}
            />
            Enable my Slack reminders
          </label>
          <label className="block text-sm">
            Slack destination ID
            <input
              className="mt-1 block h-10 w-full rounded-md border bg-background px-3"
              value={slackChannelId}
              disabled={saving}
              onChange={(event) => setSlackChannelId(event.target.value.trim())}
              placeholder="C… channel or D… direct message"
            />
          </label>
        </section>
        {error && <GraphqlError error={error} />}
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={saving || (!dirty && !!saved)}>
            {saving ? "Saving…" : "Save experience profile"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={async () => {
              if (
                dirty &&
                !window.confirm("Discard local changes and reload the latest saved profile?")
              )
                return
              try {
                loadProfile(await reload())
              } catch {
                toast.error("Could not reload your profile. Your text is still here.")
              }
            }}
          >
            Reload saved version
          </Button>
        </div>
      </form>
      <aside className="min-w-0 space-y-4">
        <h2 className="font-semibold">Version history · {saved?.revisions.length ?? 0}</h2>
        <p className="text-sm text-muted-foreground">
          Every save keeps the exact Markdown and preferences. Exports contain saved Markdown,
          including its original spacing.
        </p>
        {!saved && <p className="text-sm">Save your first version to enable export.</p>}
        {saved?.revisions.map((revision) => (
          <details key={revision.id} className="rounded-xl border p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Version {revision.number} · {formatDate(revision.createdAt)}
            </summary>
            <p className="my-3 text-sm">
              Friday {revision.fridayTime} · {revision.timeZone} · {revision.postsPerWeek}{" "}
              posts/week
            </p>
            <pre className="my-3 max-h-72 overflow-auto whitespace-pre-wrap break-words text-xs">
              {revision.markdown || "(Empty profile)"}
            </pre>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => download(revision.markdown, revision.number)}
            >
              Export version {revision.number}
            </Button>
          </details>
        ))}
      </aside>
    </div>
  )
}
